import type { AdminV2MediaMetaContext } from '@/admin-v2/tools/media-meta-context';

export type SeedanceRouteableVariant = '2.0' | '2.0-fast';
export type SeedanceDynamicProvider = 'apimart' | 'replicate';

type Resolution = '480p' | '720p' | '1080p' | '4K';

const APIMART_VIDEO_INPUT_USD_PER_SECOND = {
  '2.0': {
    '480p': 0.04,
    '720p': 0.08584,
    '1080p': 0.21568,
    '4K': 0.44432,
  },
  '2.0-fast': {
    '480p': 0.02368,
    '720p': 0.05128,
  },
} as const;

const REPLICATE_VIDEO_INPUT_USD_PER_OUTPUT_SECOND = {
  '2.0': {
    '480p': 0.1,
    '720p': 0.22,
    '1080p': 0.55,
    '4K': 1.25,
  },
  '2.0-fast': {
    '480p': 0.08,
    '720p': 0.17,
  },
} as const;

function finitePositiveNumber(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? value
    : null;
}

export function readReferenceVideoDurationSeconds(params: {
  mediaMetaContext?: AdminV2MediaMetaContext;
  fieldId?: string;
}) {
  const fieldId = params.fieldId ?? 'referenceVideos';
  const raw = params.mediaMetaContext?.clientProvidedMeta?.[fieldId];
  const items = Array.isArray(raw) ? raw : raw ? [raw] : [];
  return items.reduce((total, item) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return total;
    const duration = finitePositiveNumber(
      (item as Record<string, unknown>).durationSeconds
    );
    return total + (duration ?? 0);
  }, 0);
}

function readVariantRate<TVariant extends SeedanceRouteableVariant>(params: {
  table: typeof APIMART_VIDEO_INPUT_USD_PER_SECOND;
  variant: TVariant;
  resolution: string;
}) {
  const row = params.table[params.variant] as Partial<
    Record<Resolution, number>
  >;
  return row[params.resolution as Resolution] ?? null;
}

function readReplicateRate<TVariant extends SeedanceRouteableVariant>(params: {
  variant: TVariant;
  resolution: string;
}) {
  const row = REPLICATE_VIDEO_INPUT_USD_PER_OUTPUT_SECOND[
    params.variant
  ] as Partial<Record<Resolution, number>>;
  return row[params.resolution as Resolution] ?? null;
}

export function estimateSeedanceReferenceVideoProviderCosts(params: {
  variant: SeedanceRouteableVariant;
  resolution: string;
  outputDurationSeconds: number;
  referenceVideoDurationSeconds: number;
}) {
  const apimartRate = readVariantRate({
    table: APIMART_VIDEO_INPUT_USD_PER_SECOND,
    variant: params.variant,
    resolution: params.resolution,
  });
  const replicateRate = readReplicateRate({
    variant: params.variant,
    resolution: params.resolution,
  });
  if (
    apimartRate === null ||
    replicateRate === null ||
    !Number.isFinite(params.outputDurationSeconds) ||
    params.outputDurationSeconds <= 0 ||
    !Number.isFinite(params.referenceVideoDurationSeconds) ||
    params.referenceVideoDurationSeconds <= 0
  ) {
    return null;
  }
  return {
    apimart:
      (params.referenceVideoDurationSeconds + params.outputDurationSeconds) *
      apimartRate,
    replicate: params.outputDurationSeconds * replicateRate,
  };
}

export function resolveSeedanceDynamicProvider(params: {
  variant: SeedanceRouteableVariant;
  normalizedInput: Record<string, unknown>;
  mediaMetaContext?: AdminV2MediaMetaContext;
}): SeedanceDynamicProvider {
  const referenceVideoDurationSeconds = readReferenceVideoDurationSeconds({
    mediaMetaContext: params.mediaMetaContext,
  });
  if (referenceVideoDurationSeconds <= 0) return 'apimart';

  const outputDurationSeconds = finitePositiveNumber(
    params.normalizedInput.duration
  );
  const resolution = params.normalizedInput.resolution;
  if (!outputDurationSeconds || typeof resolution !== 'string') {
    return 'apimart';
  }

  const costs = estimateSeedanceReferenceVideoProviderCosts({
    variant: params.variant,
    resolution,
    outputDurationSeconds,
    referenceVideoDurationSeconds,
  });
  if (!costs) return 'apimart';
  return costs.replicate < costs.apimart ? 'replicate' : 'apimart';
}
